/**
 * Gate that decides whether a wall-clock delta may reach the simulation.
 *
 * A hidden tab must grant **exactly zero** progress: no damage, no kills, no
 * XP, no gold, no boss-countdown movement. Two separate things are needed for
 * that, and both live here so they can be tested without a renderer:
 *
 * 1. While paused, every delta is dropped.
 * 2. On resume, the FIRST delta is dropped too. `requestAnimationFrame` stops
 *    firing in a background tab, so the frame that arrives on return carries
 *    the whole time the tab spent hidden. Feeding it in would silently
 *    simulate those minutes - which is precisely the offline farm the SERVER
 *    is supposed to compute, never the browser.
 *
 * It also hands the simulation WHOLE milliseconds without ever rounding time
 * into existence. A 60 Hz frame is 16.666… ms; rounding each one to 17 makes
 * the run gain about 2% on the wall clock, which sounds harmless and is not:
 * the server credits `min(server elapsed, client simulated)`, so a client that
 * drifts ahead is capped every single window and its state stops matching the
 * server's re-simulation - a permanent, self-inflicted divergence. The
 * fractional remainder is carried to the next frame instead, so simulated time
 * tracks real time exactly and stays at or just below it.
 */
export class HuntClock {
  private paused = false;
  private discardNextDelta = false;
  /** Sub-millisecond remainder owed to the next frame. Never rounded away. */
  private carryMs = 0;

  /** Idempotent: repeated calls with the same value change nothing. */
  setPaused(paused: boolean): void {
    if (paused === this.paused) {
      return;
    }
    this.paused = paused;
    if (!paused) {
      // Resuming: the next frame's delta spans the hidden period. Drop it.
      this.discardNextDelta = true;
    }
  }

  isPaused(): boolean {
    return this.paused;
  }

  /** True while the next delta is still going to be discarded. */
  isDiscardingNextDelta(): boolean {
    return this.discardNextDelta;
  }

  /**
   * Returns the whole milliseconds the simulation should advance by, or `null`
   * when this frame must be skipped entirely.
   *
   * The remainder is kept, not discarded: three 16.67 ms frames hand over
   * 16, 17 and 17 ms - fifty in total, exactly the time that passed.
   */
  consume(deltaMs: number): number | null {
    if (this.paused) {
      return null;
    }
    if (this.discardNextDelta) {
      this.discardNextDelta = false;
      // The carry belonged to the frame before the tab was hidden; that time
      // is the server's to grant, not ours.
      this.carryMs = 0;
      return null;
    }
    if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
      return null;
    }
    const available = this.carryMs + deltaMs;
    const whole = Math.floor(available);
    this.carryMs = available - whole;
    return whole;
  }
}
